import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import Chart from 'chart.js/auto';
import { buildErrorTags, classifyAppError, getErrorMessage } from '@dash/randy/shared/errors/error-policy';
import { CLIENT_REQUEST_ID_HEADER, httpSpanRegistry } from '@dash/randy/shared/observability/http-span-registry';
import { isOwnedApiUrl, OWNED_API_URL_PATTERNS } from '@dash/randy/shared/observability/owned-api';
import {
  canonicalizeDashboardRoute,
  canonicalizeOwnedApiRoute,
  normalizePath,
} from '@dash/randy/shared/observability/route-pattern';
import { PAGE_LOAD_TRACE_TIMEOUT_MS } from '@dash/randy/shared/observability/page-load-tracing.service';
import { resolveTraceSampleRate } from '@dash/randy/shared/observability/trace-sampling';

if (environment.production) {
  enableProdMode();
}

Sentry.init({
  dsn: 'https://8147a8042e28f19dfc0abc2b118d8271@o4506468623974400.ingest.us.sentry.io/4506469023481856',
  enabled: environment.sentry.enabled,
  release: environment.sentry.release,
  dist: $localize?.locale,
  sendDefaultPii: false,
  sampleRate: 1.0,
  tracePropagationTargets: OWNED_API_URL_PATTERNS,
  integrations: [
    Sentry.browserTracingIntegration({
      childSpanTimeout: PAGE_LOAD_TRACE_TIMEOUT_MS + 5_000,
      enableLongAnimationFrame: true,
      enableHTTPTimings: true,
      finalTimeout: PAGE_LOAD_TRACE_TIMEOUT_MS + 5_000,
      beforeStartSpan(options) {
        const fallbackName = canonicalizeTransactionName(options.name);
        return {
          ...options,
          name: fallbackName,
          attributes: {
            ...options.attributes,
            ...getNavigationAttributes(options.op),
          },
        };
      },
      onRequestSpanStart(span, requestInformation) {
        httpSpanRegistry.registerAutomaticSpan(
          requestInformation.headers?.get(CLIENT_REQUEST_ID_HEADER) ?? null,
          span,
        );
      },
      onRequestSpanEnd(span, responseInformation) {
        httpSpanRegistry.enrichAutomaticSpan(span, responseInformation.headers);
      },
    }),
    Sentry.browserProfilingIntegration(),
  ],
  debug: environment.sentry.debug,
  environment: environment.sentry.environment,
  profileSessionSampleRate: environment.sentry.profileSessionSampleRate,
  profileLifecycle: 'trace',
  beforeBreadcrumb(breadcrumb) {
    return sanitizeBreadcrumb(breadcrumb);
  },
  beforeSendTransaction(event) {
    scrubEventRequest(event);
    for (const span of event.spans ?? []) {
      if (span.op !== 'http.client') {
        continue;
      }

      const rawUrl = span.data['http.url'] ?? span.data.url;
      if (typeof rawUrl !== 'string' || !isOwnedApiUrl(rawUrl)) {
        continue;
      }

      const parsedUrl = new URL(rawUrl, window.location.origin);
      const safeUrl = sanitizeOwnedApiSpanUrl(parsedUrl);
      const safeData = Object.fromEntries(
        Object.entries(span.data).filter(([key]) => !/(?:^|\.)(?:fragment|query)$/i.test(key)),
      );
      span.data = {
        ...safeData,
        'http.url': safeUrl,
        url: safeUrl,
      };
    }
    return event;
  },
  beforeSend(event, hint) {
    scrubEventRequest(event);
    const isExceptionEvent = !!event.exception;
    const originalException = hint?.originalException;
    const classification = isExceptionEvent && originalException ? classifyAppError(originalException) : null;

    if (classification) {
      event.tags = {
        ...event.tags,
        error_reason: event.tags?.error_reason ?? classification.reason,
        failure_kind: event.tags?.failure_kind ?? classification.failureKind,
        reporting_policy: event.tags?.reporting_policy ?? classification.reportingPolicy,
      };
    }

    if (event.tags?.reporting_policy === 'manual-only' && event.tags?.user_reported !== 'true') {
      return null;
    }

    if (!environment.production && environment.sentry.debug) {
      console.error(event, 'Unexpected application error');
    }

    return event;
  },

  tracesSampler: samplingContext =>
    samplingContext.inheritOrSampleWith(
      resolveTraceSampleRate(
        environment.sentry.traceSampleRate,
        environment.sentry.pageTraceSampleRate,
        samplingContext.location?.pathname,
        samplingContext.name,
      ),
    ),
});

Chart.defaults.color = '#878f9e';

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch(async err => {
    console.error(err);
    const captureContext = { tags: buildErrorTags(err, 'angular-bootstrap') };
    if (err instanceof Error) {
      Sentry.captureException(err, captureContext);
    } else {
      Sentry.captureMessage(getErrorMessage(err), captureContext);
    }
    await Sentry.flush(2_000);
  });

function canonicalizeTransactionName(name: string): string {
  if (/^https?:\/\//i.test(name)) {
    try {
      return canonicalizeDashboardRoute(new URL(name).pathname);
    } catch {
      return canonicalizeDashboardRoute(name);
    }
  }

  return canonicalizeDashboardRoute(name);
}

function scrubEventRequest(event: Sentry.Event): void {
  if (!event.request) {
    return;
  }

  const safeHeaders = Object.fromEntries(
    Object.entries(event.request.headers ?? {}).filter(([name]) => {
      const normalizedName = name.toLowerCase();
      return normalizedName !== 'authorization' && normalizedName !== 'cookie' && normalizedName !== 'set-cookie';
    }),
  );

  const safeRequest: Sentry.Event['request'] = {
    ...event.request,
    cookies: undefined,
    data: undefined,
    headers: safeHeaders,
    query_string: undefined,
    url: event.tags?.error_source === 'owned-api-request'
      ? sanitizeOwnedApiRequestUrlString(event.request.url)
      : sanitizePageUrl(event.request.url),
  };

  event.request = event.tags?.error_source === 'owned-api-request'
    ? { method: safeRequest.method, url: safeRequest.url }
    : safeRequest;
}

function sanitizePageUrl(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.origin}${canonicalizeDashboardRoute(parsedUrl.pathname)}`;
  } catch {
    return canonicalizeDashboardRoute(url);
  }
}

function sanitizeOwnedApiRequestUrl(url: URL): string {
  return `${url.origin}${normalizePath(url.pathname)}`;
}

function sanitizeOwnedApiSpanUrl(url: URL): string {
  return `${url.origin}${canonicalizeOwnedApiRoute(url.pathname)}`;
}

function sanitizeOwnedApiRequestUrlString(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }

  try {
    return sanitizeOwnedApiRequestUrl(new URL(url, window.location.origin));
  } catch {
    return undefined;
  }
}

function sanitizeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (containsUnsafeBreadcrumbContent(breadcrumb)) {
    return null;
  }

  const level = breadcrumb.level ?? 'info';
  if (
    breadcrumb.category === 'console'
    && (level === 'debug' || level === 'log' || level === 'info')
  ) {
    return null;
  }

  return {
    ...breadcrumb,
    data: sanitizeBreadcrumbData(breadcrumb.data, breadcrumb.category),
    message: sanitizeBreadcrumbText(breadcrumb.message),
  };
}

function containsUnsafeBreadcrumbContent(breadcrumb: Sentry.Breadcrumb): boolean {
  const candidates = [breadcrumb.message, ...Object.values(breadcrumb.data ?? {})];

  return candidates.some(value => typeof value === 'string' && isUnsafeBreadcrumbString(value));
}

function sanitizeBreadcrumbData(
  data: Record<string, unknown> | undefined,
  category: string | undefined,
): Record<string, string | number | boolean | null> | undefined {
  if (!data) {
    return undefined;
  }

  const sanitizedEntries: Array<[string, string | number | boolean | null]> = [];
  for (const [key, value] of Object.entries(data)) {
    const normalizedKey = key.toLowerCase();
    if (
      sanitizedEntries.length >= 20
      || isUnsafeBreadcrumbDataKey(key)
      || (category === 'console' && normalizedKey !== 'logger')
    ) {
      continue;
    }

    if ((normalizedKey === 'url' || normalizedKey === 'from' || normalizedKey === 'to') && typeof value === 'string') {
      const sanitizedUrl = sanitizeBreadcrumbUrl(value, category === 'xhr' || category === 'fetch');
      if (sanitizedUrl) {
        sanitizedEntries.push([key, sanitizedUrl]);
      }
      continue;
    }

    if (normalizedKey === 'route' && typeof value === 'string') {
      const canonicalRoute = value.includes('/apps/')
        ? canonicalizeOwnedApiRoute(value)
        : canonicalizeDashboardRoute(value);
      sanitizedEntries.push([key, canonicalRoute]);
      continue;
    }

    if (typeof value === 'string') {
      const sanitizedValue = sanitizeBreadcrumbText(value);
      if (sanitizedValue !== undefined) {
        sanitizedEntries.push([key, sanitizedValue]);
      }
      continue;
    }

    if (value === null) {
      sanitizedEntries.push([key, null]);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitizedEntries.push([key, value]);
    }
  }

  return sanitizedEntries.length > 0 ? Object.fromEntries(sanitizedEntries) : undefined;
}

function sanitizeBreadcrumbUrl(value: string, isHttpRequest: boolean): string | undefined {
  if (isUnsafeBreadcrumbString(value)) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(value, window.location.origin);
    return isHttpRequest && isOwnedApiUrl(parsedUrl.href)
      ? sanitizeOwnedApiSpanUrl(parsedUrl)
      : `${parsedUrl.origin}${canonicalizeDashboardRoute(parsedUrl.pathname)}`;
  } catch {
    return undefined;
  }
}

function sanitizeBreadcrumbText(value: string | undefined): string | undefined {
  if (!value) {
    return value;
  }
  if (isUnsafeBreadcrumbString(value)) {
    return undefined;
  }

  const sanitizedValue = sanitizeUrlsInText(value);
  const maximumLength = 500;
  return sanitizedValue.length <= maximumLength
    ? sanitizedValue
    : `${sanitizedValue.slice(0, maximumLength - 1)}…`;
}

function sanitizeUrlsInText(value: string): string {
  const absoluteUrlsSanitized = value.replace(/https?:\/\/[^\s"')]+/gi, candidate => {
    try {
      const parsedUrl = new URL(candidate);
      return isOwnedApiUrl(parsedUrl.href)
        ? sanitizeOwnedApiSpanUrl(parsedUrl)
        : `${parsedUrl.origin}${canonicalizeDashboardRoute(parsedUrl.pathname)}`;
    } catch {
      return '[url omitted]';
    }
  });

  return absoluteUrlsSanitized.replace(/\/apps\/[^\s"')]+/gi, candidate => {
    try {
      return canonicalizeOwnedApiRoute(new URL(candidate, window.location.origin).pathname);
    } catch {
      return '[url omitted]';
    }
  });
}

function isUnsafeBreadcrumbString(value: string): boolean {
  return /(?:^|[\s("'=])(?:data|blob):/i.test(value)
    || /;base64,/i.test(value)
    || /[a-z0-9+/]{256,}={0,2}/i.test(value);
}

function isUnsafeBreadcrumbDataKey(key: string): boolean {
  return /(?:arguments?|args|authorization|body|content|cookie|file(?:name)?|fragment|headers?|payload|query|search(?:term)?)/i.test(key)
    || /^(?:request|response)(?:[-_]?(?:body|content|data))?$/i.test(key);
}

function getNavigationAttributes(operation: string | undefined): Record<string, string> {
  if (operation === 'navigation') {
    return { 'dashboard.navigation.kind': 'internal_navigation' };
  }

  if (operation !== 'pageload') {
    return {};
  }

  const browserType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type
    ?? 'unknown';
  let navigationKind = 'page_load';
  if (browserType === 'reload') {
    navigationKind = 'page_reload';
  } else if (browserType === 'back_forward') {
    navigationKind = 'browser_history';
  }

  return {
    'dashboard.navigation.browser_type': browserType,
    'dashboard.navigation.kind': navigationKind,
  };
}
